import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Instagram, MapPin, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { SERVICES, CONTACT, TOOLS_BG } from "@/data/site";
import { Reveal } from "@/components/motion/Reveal";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "09:00","09:30","10:00","10:30","11:00","11:30",
  "12:00","12:30","13:00","13:30","14:00","14:30",
  "15:00","15:30","16:00","16:30","17:00","17:30",
  "18:00","18:30","19:00","19:30","20:00",
];

export const Booking = () => {
  const [form, setForm] = useState({ name: "", phone: "", service: "", date: undefined, time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !form.date || !form.time) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name,
        phone: form.phone,
        service: form.service,
        date: format(form.date, "yyyy-MM-dd"),
        time: form.time,
        notes: form.notes,
      });
      toast.success("Appointment requested — Jay was just pinged on his phone.");
      setForm({ name: "", phone: "", service: "", date: undefined, time: "", notes: "" });
    } catch (err) {
      toast.error("Couldn't submit booking. Try texting instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" className="section relative bg-[#0A0A0A] border-t border-[#27272A] overflow-hidden">
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{ backgroundImage: `url(${TOOLS_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="container-x relative grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.4em] text-accent mb-4">/ 05 Reserve The Chair</div>
            <h2 className="font-display text-5xl sm:text-7xl text-white leading-none mb-8" data-testid="booking-title">
              BOOK<br />YOUR CUT.
            </h2>
            <p className="text-dim text-base leading-relaxed mb-10 max-w-md">
              Pick a service, drop your details, and lock in a time. Jay gets an
              instant alert on his phone and reaches out to confirm.
            </p>
          </Reveal>

          <div className="space-y-5">
            <Reveal delay={0.1}>
              <ContactRow icon={<Instagram size={18} />} label="Instagram" value={`@${CONTACT.instagram}`} href={CONTACT.instagramUrl} testId="contact-instagram" />
            </Reveal>
            <Reveal delay={0.15}>
              <ContactRow icon={<MapPin size={18} />} label="Location" value={CONTACT.city} testId="contact-location" />
            </Reveal>
            <Reveal delay={0.2}>
              <ContactRow icon={<DollarSign size={18} />} label="Cash App" value={`$${CONTACT.cashapp}`} href={CONTACT.cashappUrl} testId="contact-cashapp" />
            </Reveal>
          </div>

          <Reveal delay={0.3}>
            <div className="mt-10 p-6 border border-[#27272A] rounded-md bg-[#121212]">
              <div className="text-xs uppercase tracking-[0.3em] text-accent mb-2">Hours</div>
              <div className="text-sm text-white/80 space-y-1 font-mono">
                <div className="flex justify-between"><span>Mon – Thu</span><span>3:30 PM – 8:00 PM</span></div>
                <div className="flex justify-between"><span>Friday</span><span>9:30 AM – 9:30 PM</span></div>
                <div className="flex justify-between"><span>Saturday</span><span>Closed</span></div>
                <div className="flex justify-between"><span>Sunday</span><span>9:30 AM – 7:30 PM</span></div>
              </div>
            </div>
          </Reveal>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="booking-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-3 bg-[#121212] border border-[#27272A] rounded-md p-8 md:p-10 space-y-5 h-fit"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Full Name *">
              <Input data-testid="booking-name-input" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" className="bg-[#0A0A0A] border-[#27272A] h-12 focus-visible:ring-accent" />
            </Field>
            <Field label="Phone *">
              <Input data-testid="booking-phone-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 555-5555" className="bg-[#0A0A0A] border-[#27272A] h-12 focus-visible:ring-accent" />
            </Field>
          </div>

          <Field label="Service *">
            <Select value={form.service} onValueChange={(v) => update("service", v)}>
              <SelectTrigger data-testid="booking-service-select" className="bg-[#0A0A0A] border-[#27272A] h-12 focus:ring-accent">
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                {SERVICES.map((s) => (
                  <SelectItem key={s.id} value={s.name} data-testid={`service-option-${s.id}`}>
                    {s.name} — ${s.price}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Date *">
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" data-testid="booking-date-trigger"
                    className={cn("w-full justify-start text-left font-normal h-12 bg-[#0A0A0A] border-[#27272A] hover:bg-[#1E1E1E]", !form.date && "text-dim")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.date ? format(form.date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121212] border-[#27272A]">
                  <Calendar mode="single" selected={form.date} onSelect={(d) => update("date", d)}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Time *">
              <Select value={form.time} onValueChange={(v) => update("time", v)}>
                <SelectTrigger data-testid="booking-time-select" className="bg-[#0A0A0A] border-[#27272A] h-12 focus:ring-accent">
                  <SelectValue placeholder="Pick a time" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A] text-white max-h-60">
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`time-option-${t}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Notes (optional)">
            <Textarea data-testid="booking-notes-input" value={form.notes} onChange={(e) => update("notes", e.target.value)}
              placeholder="Any specific style, fade level, or requests?"
              className="bg-[#0A0A0A] border-[#27272A] focus-visible:ring-accent min-h-[100px]" />
          </Field>

          <Button type="submit" disabled={submitting} data-testid="booking-submit-button"
            className="w-full h-14 bg-accent hover:bg-[#D90429] text-white font-bold uppercase tracking-[0.25em] text-sm">
            {submitting ? "Sending..." : "Request Appointment →"}
          </Button>
          <p className="text-xs text-dim text-center">
            Jay gets an instant push notification and will text you to confirm.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.25em] text-dim mb-2 block">{label}</Label>
    {children}
  </div>
);

const ContactRow = ({ icon, label, value, href, testId }) => {
  const inner = (
    <div className="flex items-center gap-4 group">
      <div className="w-11 h-11 rounded-md border border-[#27272A] bg-[#121212] flex items-center justify-center text-accent group-hover:border-accent group-hover:bg-accent/5 transition-colors">
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-dim">{label}</div>
        <div className="text-white font-semibold">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testId}>{inner}</a>
  ) : (
    <div data-testid={testId}>{inner}</div>
  );
};
